import type { ShopDailyMetric } from '@/modules/shop/types'

export type MetricFormat = 'currency' | 'number' | 'percent' | 'ratio'
export type MetricAggregation = 'sum' | 'average' | 'ratio'

export interface ShopMetricDefinition {
  key: keyof Omit<ShopDailyMetric, 'date'>
  label: string
  format: MetricFormat
  aggregation: MetricAggregation
  /**
   * For 'ratio' metrics: the headline is sum(numerator) / sum(denominator)
   * across the period. Averaging a pre-computed daily rate is wrong — it
   * weights a 10-visitor day the same as a 4,000-visitor day.
   */
  ratio?: {
    numerator: keyof Omit<ShopDailyMetric, 'date'>
    denominator: keyof Omit<ShopDailyMetric, 'date'>
  }
  /** Explanatory tooltip shown on the KPI card - use to differentiate similar metrics. */
  description?: string
  /** True when the underlying field is not yet ingested and is surfaced as zero/placeholder. */
  isDemo?: boolean
}

export const SHOP_METRIC_CATALOG: ShopMetricDefinition[] = [
  { key: 'gmv', label: 'GMV', format: 'currency', aggregation: 'sum',
    description: 'Gross merchandise value: total paid at checkout across the whole shop, all channels, before refunds.' },
  { key: 'grossRevenue', label: 'Shop Gross Revenue', format: 'currency', aggregation: 'sum',
    description: "TikTok shop analytics' gross revenue: customer payment plus platform subsidies minus taxes. Not ad-attributed." },
  { key: 'orders', label: 'Orders', format: 'number', aggregation: 'sum' },
  { key: 'customers', label: 'Customers', format: 'number', aggregation: 'sum' },
  { key: 'visitors', label: 'Visitors', format: 'number', aggregation: 'sum' },
  { key: 'conversionRate', label: 'CVR', format: 'percent', aggregation: 'ratio', ratio: { numerator: 'orders', denominator: 'visitors' } },
  { key: 'itemsSold', label: 'Items Sold', format: 'number', aggregation: 'sum' },
  { key: 'pageViews', label: 'Page Views', format: 'number', aggregation: 'sum' },
  { key: 'skuOrders', label: 'SKU Orders', format: 'number', aggregation: 'sum' },
  { key: 'liveGmv', label: 'LIVE GMV', format: 'currency', aggregation: 'sum' },
  { key: 'videoGmv', label: 'Video GMV', format: 'currency', aggregation: 'sum' },
  { key: 'productCardGmv', label: 'Product Card GMV', format: 'currency', aggregation: 'sum' },
  { key: 'adRevenue', label: 'Ad-Attributed Revenue', format: 'currency', aggregation: 'sum',
    description: 'Revenue TikTok attributes to your ad campaigns — standard auction campaigns plus GMV Max. A subset of GMV, not the same thing.' },
  { key: 'gmvMaxSpend', label: 'GMV Max Spend', format: 'currency', aggregation: 'sum',
    description: 'Ad spend on GMV Max campaigns. See the Ads page for the full campaign-type split with attributed revenue and ROI.' },
  { key: 'itemsRefunded', label: 'Items Refunded', format: 'number', aggregation: 'sum' },
  { key: 'itemsCancelled', label: 'Items Cancelled', format: 'number', aggregation: 'sum' },
  { key: 'sellerFundedDiscount', label: 'Discounts You Funded', format: 'currency', aggregation: 'sum',
    description: 'Promotional discounts the brand paid for out of its own margin (seller-funded product + shipping discounts). This is money you subsidized, not what TikTok covered.' },
  { key: 'platformFundedDiscount', label: 'Discounts TikTok Funded', format: 'currency', aggregation: 'sum',
    description: 'Discounts TikTok subsidized on your behalf — platform + payment-method + shipping promotions, including the co-funded shipping portion. Money you did not pay for.' },
]

export const DEFAULT_KPI_METRICS: Array<ShopMetricDefinition['key']> = [
  'gmv',
  'adRevenue',
  'orders',
  'customers',
  'visitors',
  'conversionRate',
  'gmvMaxSpend',
  'sellerFundedDiscount',
  'platformFundedDiscount',
]

export interface ChartTabDefinition {
  key: 'revenue' | 'orders' | 'traffic' | 'channels'
  label: string
}

export const CHART_TAB_CATALOG: ChartTabDefinition[] = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'orders', label: 'Orders' },
  { key: 'traffic', label: 'Traffic' },
  { key: 'channels', label: 'Channels' },
]

export const DEFAULT_CHART_TABS: Array<ChartTabDefinition['key']> = [
  'revenue',
  'orders',
  'traffic',
  'channels',
]

export interface ChannelSectionDefinition {
  key: 'gmv-by-channel' | 'affiliate-vs-open'
  label: string
}

export const CHANNEL_SECTION_CATALOG: ChannelSectionDefinition[] = [
  { key: 'gmv-by-channel', label: 'GMV by Channel' },
  { key: 'affiliate-vs-open', label: 'Affiliate vs Open Collaboration' },
]

export const DEFAULT_CHANNEL_SECTIONS: Array<ChannelSectionDefinition['key']> = [
  'gmv-by-channel',
  'affiliate-vs-open',
]

export function aggregate(
  data: ShopDailyMetric[],
  definition: ShopMetricDefinition,
): number {
  if (data.length === 0) return 0
  if (definition.aggregation === 'ratio' && definition.ratio) {
    const { numerator, denominator } = definition.ratio
    const num = data.reduce((acc, d) => acc + ((d[numerator] as number) ?? 0), 0)
    const den = data.reduce((acc, d) => acc + ((d[denominator] as number) ?? 0), 0)
    return den > 0 ? num / den : 0
  }
  const values = data.map((d) => d[definition.key] as number)
  const total = values.reduce((acc, v) => acc + (v ?? 0), 0)
  return definition.aggregation === 'average' ? total / data.length : total
}

export function getMetricDefinition(
  key: ShopMetricDefinition['key'],
): ShopMetricDefinition | undefined {
  return SHOP_METRIC_CATALOG.find((m) => m.key === key)
}
